import { useState } from "react";
import PropTypes from "prop-types";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import ActionDialog from "components/admin/ActionDialog";
import AdminTable from "components/admin/AdminTable";
import { useFetch, useRunner } from "components/admin/hooks";
import api from "api/client";
import { useCan } from "auth/AuthContext";
import { PERMISSIONS } from "auth/access";
import { PERIODS, parseJson, showTime } from "./shared";

function minutesText(value) {
  const n = Number(value) || 0;
  const days = Math.floor(n / 1440);
  const hours = Math.floor((n % 1440) / 60);
  const minutes = n % 60;
  const parts = [];
  if (days) parts.push(`${days} ngày`);
  if (hours) parts.push(`${hours} giờ`);
  if (minutes) parts.push(`${minutes} phút`);
  return parts.join(" ") || "0";
}

function RuleDialog({ rule, kinds, onClose, onDone }) {
  const [run, busy] = useRunner();
  const [form, setForm] = useState(() => ({
    kind: rule?.kind || kinds[0]?.key || "",
    period: rule?.period || "month",
    startOffset: rule?.start_offset ?? 0,
    duration: rule?.duration ?? 0,
    viewDays: rule?.view_days ?? 1,
    namePattern: rule?.name_pattern || "Tháng {MM}/{yyyy}",
    config: rule?.config ? JSON.stringify(parseJson(rule.config, {}), null, 2) : "",
  }));
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const configError =
    form.config.trim() && parseJson(form.config) === null ? "Không phải JSON hợp lệ" : null;

  const submit = async () => {
    const body = {
      kind: form.kind,
      period: form.period,
      startOffset: Number(form.startOffset) || 0,
      duration: Number(form.duration) || 0,
      viewDays: Number(form.viewDays) || 0,
      namePattern: form.namePattern,
      config: form.config.trim() ? parseJson(form.config) : null,
    };
    const { ok } = await run(
      () =>
        rule
          ? api.put(`/api/admin/event/rules/${rule.id}`, body)
          : api.post("/api/admin/event/rules", body),
      rule ? "Đã lưu quy tắc" : "Đã tạo quy tắc"
    );
    if (ok) {
      onClose();
      onDone();
    }
  };

  return (
    <ActionDialog
      open
      maxWidth="md"
      title={rule ? `Sửa quy tắc #${rule.id}` : "Tạo quy tắc lặp"}
      description="Server sinh trước mùa của kỳ kế tiếp (mỗi giờ kiểm một lần), để admin còn sửa mốc quà trước khi mùa chạy. Sửa quy tắc chỉ ảnh hưởng mùa sinh sau."
      color="info"
      submitText={rule ? "Lưu" : "Tạo"}
      busy={busy}
      disabled={Boolean(configError) || !form.kind || !form.namePattern}
      onClose={onClose}
      onSubmit={submit}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <MDInput
            select
            fullWidth
            label="Loại event"
            value={form.kind}
            onChange={(e) => set({ kind: e.target.value })}
          >
            {kinds.map((kind) => (
              <MenuItem key={kind.key} value={kind.key}>
                {kind.name} ({kind.key})
              </MenuItem>
            ))}
          </MDInput>
        </Grid>
        <Grid item xs={6} md={3}>
          <MDInput
            select
            fullWidth
            label="Kỳ"
            value={form.period}
            onChange={(e) => set({ period: e.target.value })}
          >
            {Object.entries(PERIODS).map(([key, label]) => (
              <MenuItem key={key} value={key}>
                {label}
              </MenuItem>
            ))}
          </MDInput>
        </Grid>
        <Grid item xs={6} md={4}>
          <MDInput
            fullWidth
            label="Mẫu tên"
            value={form.namePattern}
            helperText="{yyyy} năm, {MM} tháng của kỳ"
            onChange={(e) => set({ namePattern: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MDInput
            fullWidth
            type="number"
            label="Bắt đầu sau đầu kỳ (phút)"
            value={form.startOffset}
            inputProps={{ min: 0 }}
            helperText={`= ${minutesText(form.startOffset)}. Tuần tính từ thứ 2 00:00`}
            onChange={(e) => set({ startOffset: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MDInput
            fullWidth
            type="number"
            label="Thời lượng (phút, 0 = tới hết kỳ)"
            value={form.duration}
            inputProps={{ min: 0 }}
            helperText={Number(form.duration) ? `= ${minutesText(form.duration)}` : "Tới hết kỳ"}
            onChange={(e) => set({ duration: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MDInput
            fullWidth
            type="number"
            label="Ngày xem sau khi hết"
            value={form.viewDays}
            inputProps={{ min: 0 }}
            helperText="Mùa kỳ sau bắt đầu thì phần xem của kỳ trước tự đóng"
            onChange={(e) => set({ viewDays: e.target.value })}
          />
        </Grid>
      </Grid>
      <MDInput
        fullWidth
        multiline
        minRows={6}
        maxRows={20}
        label="Nâng cao (kỹ thuật): cấu hình riêng — để trống là dùng mặc định của loại"
        value={form.config}
        error={Boolean(configError)}
        helperText={
          configError ||
          'Vd mùa có mốc quà sẵn: {"rewards": [...]} hoặc khuyến mãi {"modules": {"exchange_bonus": {"percent": 10}}}'
        }
        inputProps={{ style: { fontFamily: "monospace", fontSize: 12 } }}
        onChange={(e) => set({ config: e.target.value })}
      />
    </ActionDialog>
  );
}

RuleDialog.defaultProps = { rule: null };

RuleDialog.propTypes = {
  rule: PropTypes.shape({
    id: PropTypes.number,
    kind: PropTypes.string,
    period: PropTypes.string,
    start_offset: PropTypes.number,
    duration: PropTypes.number,
    view_days: PropTypes.number,
    name_pattern: PropTypes.string,
    config: PropTypes.string,
  }),
  kinds: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string, name: PropTypes.string }))
    .isRequired,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
};

function RulesPanel() {
  const canEdit = useCan(PERMISSIONS.EVENT_EDIT);
  const [run, busy] = useRunner();
  const kinds = useFetch(() => api.get("/api/admin/event/kinds"), []);
  const rules = useFetch(() => api.get("/api/admin/event/rules"), []);
  const [dialog, setDialog] = useState(null);

  return (
    <>
      {rules.error && <Alert severity="error">{rules.error}</Alert>}
      {canEdit && (
        <MDBox mb={2}>
          <MDButton
            color="info"
            disabled={!kinds.data?.length}
            onClick={() => setDialog({ rule: null })}
          >
            Tạo quy tắc
          </MDButton>
        </MDBox>
      )}
      <AdminTable
        loading={rules.loading}
        rows={rules.data || []}
        rowKey="id"
        emptyText="Chưa có quy tắc lặp"
        columns={[
          { key: "name_pattern", label: "Mẫu tên" },
          {
            key: "kind",
            label: "Loại",
            render: (row) => (kinds.data || []).find((k) => k.key === row.kind)?.name || row.kind,
          },
          { key: "period", label: "Kỳ", render: (row) => PERIODS[row.period] || row.period },
          {
            key: "start_offset",
            label: "Bắt đầu",
            render: (row) => `+${minutesText(row.start_offset)}`,
          },
          {
            key: "duration",
            label: "Thời lượng",
            render: (row) => (Number(row.duration) ? minutesText(row.duration) : "Hết kỳ"),
          },
          { key: "view_days", label: "Ngày xem", align: "right" },
          {
            key: "generated_to",
            label: "Đã sinh tới",
            render: (row) => showTime(row.generated_to) || "Chưa",
          },
          {
            key: "enabled",
            label: "Trạng thái",
            render: (row) => (
              <Chip
                size="small"
                color={Number(row.enabled) ? "success" : "default"}
                label={Number(row.enabled) ? "Bật" : "Tắt"}
              />
            ),
          },
          {
            key: "act",
            label: "",
            render: (row) =>
              canEdit ? (
                <MDBox display="flex" gap={0.5}>
                  <MDButton
                    size="small"
                    variant="text"
                    color="info"
                    onClick={() => setDialog({ rule: row })}
                  >
                    Sửa
                  </MDButton>
                  <MDButton
                    size="small"
                    variant="text"
                    color={Number(row.enabled) ? "error" : "success"}
                    disabled={busy}
                    onClick={() =>
                      run(
                        async () => {
                          await api.post(`/api/admin/event/rules/${row.id}/toggle`);
                          rules.reload();
                        },
                        Number(row.enabled) ? "Đã tắt quy tắc" : "Đã bật quy tắc"
                      )
                    }
                  >
                    {Number(row.enabled) ? "Tắt" : "Bật"}
                  </MDButton>
                </MDBox>
              ) : null,
          },
        ]}
      />
      {dialog && (
        <RuleDialog
          rule={dialog.rule}
          kinds={kinds.data || []}
          onClose={() => setDialog(null)}
          onDone={() => rules.reload()}
        />
      )}
    </>
  );
}

export default RulesPanel;
